// src/Theme.js
import React, { createContext, useMemo, useState, useEffect } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Create a context to hold the color mode toggle function.
export const ColorModeContext = createContext({ toggleColorMode: () => {} });

const MyThemeProvider = ({ children }) => {
  // Initialize mode based on localStorage (or default to "light").
  const [mode, setMode] = useState(() => {
    const savedPreference = localStorage.getItem("preferredTheme");
    return savedPreference ? savedPreference : "light";
  });

  // Whenever the mode changes, store it in localStorage.
  useEffect(() => {
    localStorage.setItem("preferredTheme", mode);
  }, [mode]);

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () =>
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light")),
    }),
    []
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: "#AFE1AF",
          },
          secondary: {
            main: "#ECFFDC",
          },
          background: {
            default: mode === "light" ? "#f4f6f8" : "#121212",
          },
          // Custom property for header text color.
          header: {
            text: mode === "light" ? "#000" : "#fff",
          },
        },
        typography: {
          fontFamily: "'Pacifico', cursive",
          h6: {
            fontWeight: 600,
          },
          button: {
            textTransform: "none",
          },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 8,
                padding: "8px 16px",
              },
            },
          },
          MuiAppBar: {
            styleOverrides: {
              root: {
                boxShadow: "none",
              },
            },
          },
          MuiContainer: {
            styleOverrides: {
              root: {
                paddingTop: "20px",
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                padding: "20px",
                borderRadius: "12px",
              },
            },
          },
        },
      }),
    [mode]
  );

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default MyThemeProvider;